import { z } from 'zod'
import { Inv3510StatusSchema } from './list.schema'

export enum ZoneStatus {
  NotStarted = 1,
  InProgress = 2,
  Completed = 3,
}

export const ZoneStatusSchema = z.nativeEnum(ZoneStatus)

export enum ItemType {
  Normal = 1,
  Extra = 2,
  Missing = 3,
}

export const ItemTypeSchema = z.nativeEnum(ItemType)

export const Inv3510ZoneSchema = z.object({
  id: z.string(),
  name: z.string(),
  teamName: z.string(),
  estimatedHours: z.number(),
  locationCodes: z.array(z.string()),
  status: ZoneStatusSchema,
  totalItems: z.number(),
  completedItems: z.number(),
  progressPercentage: z.number(),
  hasTemplate: z.boolean(),
  hasUploadedFile: z.boolean(),
  canSubmit: z.boolean(),
  canForceSubmit: z.boolean(),
  pendingItemCount: z.number(),
  requestForRecount: z.boolean().default(false),
  requestForRecountAt: z.coerce.date().nullable().optional(),
  recountReason: z.string().nullable().optional(),
  recountRequestedBy: z.number().nullable().optional(),
  recountAttempts: z.number().default(0),
})

export type Inv3510Zone = z.infer<typeof Inv3510ZoneSchema>

// Note: Item and Variance schemas kept for other endpoints (variances, zones, etc.)
// that may still need these detailed schemas
export const Inv3510ItemSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  itemCode: z.string(),
  itemName: z.string(),
  lotNo: z.string().nullable(),
  lotDate: z.coerce.date().nullable(),
  mold: z.string().nullable(),
  locationCode: z.string(),
  systemQuantity: z.number().nullable(),
  actualQuantity: z.number().nullable(),
  unitName: z.string(),
  itemType: ItemTypeSchema,
  variance: z.number(),
  variancePercentage: z.number(),
})

export type Inv3510Item = z.infer<typeof Inv3510ItemSchema>

export const Inv3510VarianceSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  itemCode: z.string(),
  itemName: z.string(),
  lotNo: z.string().nullable(),
  locationCode: z.string(),
  systemQuantity: z.number(),
  actualQuantity: z.number(),
  variance: z.number(),
  variancePercentage: z.number(),
  varianceAmount: z.number(),
  status: z.enum(['PendingReview', 'Approved', 'Rejected']),
  itemType: ItemTypeSchema,
})

export type Inv3510Variance = z.infer<typeof Inv3510VarianceSchema>

export const Inv3510GetResultSchema = z.object({
  id: z.string(),
  number: z.string(),
  description: z.string(),
  scheduledDate: z.coerce.date(),
  status: Inv3510StatusSchema,
  zones: z.array(Inv3510ZoneSchema),
  varianceCount: z.number(),
  canEdit: z.boolean(),
  canSchedule: z.boolean(),
  canStart: z.boolean(),
  canComplete: z.boolean(),
  canCancel: z.boolean(),
  createdAt: z.coerce.date(),
  createdBy: z.number(),
})

export type Inv3510GetResult = z.infer<typeof Inv3510GetResultSchema>
